import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCognitoUser } from 'src/hooks/use-cognito-user';
import { useSafeLocalStorage } from 'src/hooks/use-storage';
import { assertContext } from 'src/utils/assert-context';
import { z } from 'zod';

export const authStorageKey = 'prisma.auth';

const accountSchema = z.object({
  cognitoUserName: z.string(),
  email: z.string(),
  organisationName: z.string().nullable(),
});

export type AuthAccount = z.output<typeof accountSchema>;

const authDataSchema = z.object({
  accounts: z.array(accountSchema).default([]),
  current: z.string().nullable().default(null),
});

export type AuthData = z.output<typeof authDataSchema>;

function useAccountsManager() {
  const cognitoUser = useCognitoUser();
  const safeLocalStorage = useSafeLocalStorage();

  const [authData, setAuthData] = useState<AuthData>(() => {
    const authStorage = safeLocalStorage.getItem(authStorageKey);
    try {
      return authDataSchema.parse(JSON.parse(authStorage ?? '{}'));
    } catch (err) {
      return { accounts: [], current: null };
    }
  });

  useEffect(() => {
    safeLocalStorage.setItem(authStorageKey, JSON.stringify(authData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData]);

  const value = useMemo(
    () => ({
      current:
        authData.accounts.find(
          (account) => account.cognitoUserName === cognitoUser?.cognitoUserName
        ) ?? null,
      add: (accountData: AuthAccount) => {
        const existingAccount = authData.accounts.find(
          (account) => account.cognitoUserName === accountData.cognitoUserName
        );
        if (existingAccount) return;

        setAuthData((authData) => ({
          ...authData,
          accounts: [...authData.accounts, accountData],
        }));
      },
      remove: (cognitoUserName: AuthAccount['cognitoUserName']) => {
        setAuthData((authData) => ({
          ...authData,
          accounts: authData.accounts.filter(
            (account: AuthAccount) =>
              account.cognitoUserName !== cognitoUserName
          ),
        }));
      },
    }),
    [cognitoUser, authData]
  );

  return value;
}

type AccountsContextType = ReturnType<typeof useAccountsManager>;

// pass undefined as any, because we run assertContext at runtime
const AccountsContext = createContext<AccountsContextType>(undefined as any);

export const AccountsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const accountsManager = useAccountsManager();
  return (
    <AccountsContext.Provider value={accountsManager}>
      {children}
    </AccountsContext.Provider>
  );
};

export const useAccounts = () => {
  const context = useContext(AccountsContext);
  assertContext(context, 'Accounts');
  return context;
};
